import {transformPortalPoint, transformPortalVector} from './portalMath.js';
import {portalCopyIsShadow} from './PortalGhostWorld.js';

// A copy fixture (ghost or shadow) stands for the real fixture it was cloned from.
export const canonicalPortalFixture = fixture => fixture?._portalGhostOf || fixture;

export const canonicalPortalBody = body => body?._portalGhostOf || body;

const ghostBodyOf = fixture => {
	const body = fixture?.GetBody?.();
	return body?.isPortalGhost ? body : null;
};

const readVec = vector => ({x: vector.get_x(), y: vector.get_y()});

// Gameplay sees a ghost contact as a contact with the real far-side fixture.
// Its manifold is reported where the impact really happened: at the exit,
// which is where the travelling part is drawn.
const createContactView = (Box2D, fixtureA, fixtureB, rawContact, mapping = null) => {
	const overrides = {
		isPortalGhostView: true,
		GetFixtureA: () => fixtureA,
		GetFixtureB: () => fixtureB,
	};
	if(mapping) {
		overrides.GetWorldManifold = worldManifold => {
			rawContact.GetWorldManifold(worldManifold);
			const count = rawContact.GetManifold().get_pointCount();
			for(let index = 0; index < count; index++) {
				const point = worldManifold.get_points(index);
				const mapped = transformPortalPoint(
					readVec(point),
					mapping.sourcePosition,
					mapping.sourceAngle,
					mapping.targetPosition,
					mapping.targetAngle,
				);
				point.set_x(mapped.x);
				point.set_y(mapped.y);
			}
			const mappedNormal = transformPortalVector(
				readVec(worldManifold.get_normal()),
				mapping.sourceAngle,
				mapping.targetAngle,
			);
			const normal = new Box2D.b2Vec2(mappedNormal.x, mappedNormal.y);
			worldManifold.set_normal(normal);
			Box2D.destroy(normal);
		};
	}
	return new Proxy(overrides, {
		get(target, property) {
			if(property in target) return target[property];
			const value = rawContact?.[property];
			return typeof value === 'function' ? value.bind(rawContact) : value;
		},
	});
};

export class PortalContactRouter {
	constructor(Box2D, options = {}) {
		this.Box2D = Box2D;
		this.dispatch = options.dispatch || (() => {});
		this.getFrame = options.getFrame || (() => 0);
		// Clips a raw ghost contact by the portal planes before gameplay sees it.
		this.clipContact = options.clipContact || (() => false);
		this.entries = new Map();
		// One physical contact can be solved twice, once on each side; gameplay
		// hears its impulse once per step.
		this.postSolvedFrames = new Map();
	}

	mappingFor(candidate) {
		const source = candidate.source.body;
		const target = candidate.target.body;
		return {
			sourcePosition: readVec(source.GetPosition()),
			sourceAngle: source.GetAngle(),
			targetPosition: readVec(target.GetPosition()),
			targetAngle: target.GetAngle(),
		};
	}

	/**
	 * Classifies a raw contact. `null` means the contact is none of this
	 * router's business and bubbles normally. `{swallow: true}` means it must
	 * not reach gameplay at all (a ghost meeting something that is not its
	 * traveller). Otherwise the info carries the canonical view to dispatch.
	 */
	contactInfo(contact, allowSolid = false) {
		if(!contact?.GetFixtureA || !contact?.GetFixtureB) return null;
		const rawFixtureA = contact.GetFixtureA();
		const rawFixtureB = contact.GetFixtureB();
		const rawBodyA = rawFixtureA.GetBody();
		const rawBodyB = rawFixtureB.GetBody();
		const ghostA = ghostBodyOf(rawFixtureA);
		const ghostB = ghostBodyOf(rawFixtureB);
		if(ghostA && ghostB) return {swallow: true, ghostPair: true};
		const ghost = ghostA || ghostB;
		const candidateA = rawBodyA._portalTransitCandidate || null;
		const candidateB = rawBodyB._portalTransitCandidate || null;

		if(ghost) {
			const realBody = ghostA ? rawBodyB : rawBodyA;
			const travellerCandidate = ghostA ? candidateB : candidateA;
			const fixtureA = canonicalPortalFixture(rawFixtureA);
			const fixtureB = canonicalPortalFixture(rawFixtureB);
			const key = this.keyFor(fixtureA, fixtureB);
			const shadow = portalCopyIsShadow(ghost);
			const copyCandidate = ghost._portalGhostOf?._portalTransitCandidate || null;
			if(realBody.isPortalSurface || realBody.isPhysicsCamera) return {swallow: true, key, copy: true};
			if(travellerCandidate) {
				// A traveller's crossed part meets the far room through copies
				// behind its entrance. The impact is drawn at its exit.
				const onSide = travellerCandidate.source === ghost._portalGhostSide &&
					!(shadow && copyCandidate === travellerCandidate);
				if(travellerCandidate.contactRouter !== this) return onSide ? null : {swallow: true, key, copy: true};
				return {
					key,
					candidate: travellerCandidate,
					ghost: true,
					copy: true,
					swallow: !onSide,
					view: onSide ?
						createContactView(this.Box2D, fixtureA, fixtureB, contact, this.mappingFor(travellerCandidate)) :
						null,
				};
			}
			// An ordinary body meets an emerging island on its shadow, right where
			// the island is drawn.
			if(!shadow || !copyCandidate) return {swallow: true, key, copy: true};
			if(copyCandidate.contactRouter !== this) return null;
			return {
				key,
				candidate: copyCandidate,
				ghost: true,
				copy: true,
				swallow: false,
				view: createContactView(this.Box2D, fixtureA, fixtureB, contact),
			};
		}

		if(!allowSolid && !rawFixtureA.IsSensor() && !rawFixtureB.IsSensor()) return null;
		// Portal volume sensors drive transit admission through their ordinary
		// listener and must never be replayed as gameplay trigger events.
		if(rawBodyA.isPortalSurface || rawBodyB.isPortalSurface ||
			rawBodyA.isPhysicsCamera || rawBodyB.isPhysicsCamera) {
			return null;
		}
		if(candidateA && candidateA === candidateB) return null;
		const candidate = [candidateA, candidateB].find(entry => entry?.contactRouter === this);
		if(!candidate) return null;
		return {
			key: this.keyFor(rawFixtureA, rawFixtureB),
			candidate,
			ghost: false,
			view: createContactView(this.Box2D, rawFixtureA, rawFixtureB, contact),
		};
	}

	keyFor(fixtureA, fixtureB) {
		const pointerA = this.Box2D.getPointer(fixtureA);
		const pointerB = this.Box2D.getPointer(fixtureB);
		return pointerA < pointerB ? `${pointerA}:${pointerB}` : `${pointerB}:${pointerA}`;
	}

	entryFor(info) {
		let entry = this.entries.get(info.key);
		if(!entry) {
			entry = {
				key: info.key,
				candidate: info.candidate,
				representations: new Set(),
				view: info.view,
				dispatched: false,
				emptySinceFrame: null,
			};
			this.entries.set(info.key, entry);
		}
		entry.candidate = info.candidate;
		if(info.view) entry.view = info.view;
		return entry;
	}

	addRepresentation(info, representation, silent = false) {
		const entry = this.entryFor(info);
		entry.representations.add(representation);
		entry.emptySinceFrame = null;
		if(entry.dispatched) return;
		entry.dispatched = true;
		if(!silent) this.dispatch('BeginContact', entry.view);
	}

	removeRepresentation(entry, representation, frame = this.getFrame()) {
		if(!entry?.representations.delete(representation)) return;
		if(entry.representations.size) return;
		entry.emptySinceFrame = frame;
	}

	/**
	 * Routes one contact event. Returns true when the event was handled here
	 * (dispatched with canonical fixtures, deduplicated, or swallowed) and
	 * false when it should bubble through the ordinary listener chain.
	 */
	route(name, contact, secondParam = undefined, silent = false) {
		const begin = name === 'BeginContact';
		const end = name === 'EndContact';
		const info = this.contactInfo(contact);
		if(!info) return false;
		if(!(begin || end)) {
			// Real contacts keep their ordinary PreSolve/PostSolve path, including
			// the plane clipping in PortalBridge. A ghost contact is clipped by the
			// portal planes here (which also disables one that is not on its
			// traveller's side), then gameplay judges the real bodies. Two ghosts
			// of one side simply collide; two of different sides are disabled.
			if(info.ghostPair) {
				if(name === 'PreSolve' && this.clipContact(contact)) contact.SetEnabled(false);
				return true;
			}
			// Every contact of a copy is clipped by the portal rules first, whether
			// or not gameplay will hear it (a copy resting on its portal's lip is
			// solved but silent).
			if(info.copy && name === 'PreSolve' && this.clipContact(contact)) {
				contact.SetEnabled(false);
				return true;
			}
			if(!info.ghost) return info.swallow ? true : false;
			if(info.swallow) return true;
			if(name === 'PostSolve') {
				const frame = this.getFrame();
				if(this.postSolvedFrames.get(info.key) === frame) return true;
				this.postSolvedFrames.set(info.key, frame);
			}
			if(secondParam !== undefined) this.dispatch(name, info.view, secondParam);
			else this.dispatch(name, info.view);
			return true;
		}
		// The same logical overlap can be represented by a real contact, by a
		// ghost contact and by a shadow contact, and it switches between them at
		// an ownership transfer. Begin once, end once: each raw contact is its
		// own representation of the logical pair.
		if(!info.key) return true;
		const representation = info.ghost ?
			`ghost:${info.key}:${this.Box2D.getPointer(contact.GetFixtureA())}:${this.Box2D.getPointer(contact.GetFixtureB())}` :
			`primary:${info.key}`;
		if(begin) {
			if(!info.swallow) this.addRepresentation(info, representation, silent);
		} else {
			this.removeRepresentation(this.entries.get(info.key), representation);
		}
		return true;
	}

	seedPrimaryContacts(world, candidate) {
		for(let contact = world.GetContactList();
			contact && this.Box2D.getPointer(contact) !== this.Box2D.getPointer(this.Box2D.NULL);
			contact = contact.GetNext()) {
			if(!contact.IsTouching()) continue;
			const info = this.contactInfo(contact);
			if(info?.candidate === candidate) this.route('BeginContact', contact, undefined, true);
		}
	}

	flush(frame = this.getFrame()) {
		for(const [key, stamp] of this.postSolvedFrames) {
			if(stamp < frame) this.postSolvedFrames.delete(key);
		}
		for(const [key, entry] of this.entries) {
			if(entry.representations.size || entry.emptySinceFrame === null ||
				frame <= entry.emptySinceFrame) {
				continue;
			}
			if(entry.dispatched) this.dispatch('EndContact', entry.view);
			this.entries.delete(key);
		}
	}

	releaseCandidate(candidate) {
		for(const [key, entry] of this.entries) {
			if(entry.candidate !== candidate) continue;
			const primaryStillActive = [...entry.representations].some(
				representation => representation.startsWith('primary:'),
			);
			if(!primaryStillActive && entry.dispatched) {
				this.dispatch('EndContact', entry.view);
			}
			// A primary contact continues through the normal dispatcher after transit;
			// leave its logical overlap active and let that later raw EndContact close it.
			this.entries.delete(key);
		}
	}

	destroy() {
		for(const entry of this.entries.values()) {
			if(entry.dispatched) this.dispatch('EndContact', entry.view);
		}
		this.entries.clear();
		this.postSolvedFrames.clear();
	}
}

// Finds the router responsible for a raw contact: the bridge whose portal owns
// the ghost, or the bridge whose transit owns a travelling body.
export const routePrimaryPortalContact = (name, contact, secondParam = undefined) => {
	const fixtures = [contact.GetFixtureA(), contact.GetFixtureB()];
	for(const fixture of fixtures) {
		const body = fixture.GetBody();
		if(body.isPortalGhost) {
			const router = body._portalGhostSide?.bridge?.contactRouter;
			// A copy whose bridge is already gone must not reach gameplay.
			return router ? router.route(name, contact, secondParam) || true : true;
		}
	}
	for(const fixture of fixtures) {
		const router = fixture.GetBody()._portalTransitCandidate?.contactRouter;
		if(router?.route(name, contact, secondParam)) return true;
	}
	return false;
};
